import { NextPage } from 'next';
import React from 'react';
import { Header } from 'components/structures';
import {
  commitmentSafetyExcellence,
  comprehensiveFiberFTTXSolutions,
  engineeringExpertise,
  iSPConstr,
  maintenanceRestoration,
  oSPConstr,
  precisionSplicingNetworkActivation,
  wirelineConstructionHeroSection,
} from '../images/expertise/wireline-construction';
import { SafetyAndExcellence } from '../components/structures/wireline-construction';
import TextBlockWithImage from '../components/structures/wireline-construction/TextBlockWithImage';
import { Box } from '@material-ui/core';

const list1 = [
  {
    title: 'Aerial Construction',
    description:
      'We specialize in pole installation, maintenance, and replacement, as well as lashing and over-lashing methodologies to securely attach fiber and copper lines. Our make-ready work prepares existing infrastructure for new installations, ensuring a smooth and efficient setup process.',
  },
  {
    title: 'Underground Construction',
    description:
      'We specialize in trenching, plowing, and horizontal directional drilling to efficiently install fiber and copper lines. Our vacuum excavation techniques ensure precise placement without disruption. We perform both conventional conduit and microduct installations, along with manhole and handhole installations. Additionally, we utilize advanced techniques and equipment for cable pulling, ensuring secure and precise connections.',
  },
];

const list2 = [
  {
    title: 'Network Infrastructure Installation',
    description:
      'We design and implement advanced voice and data networks within buildings, data centers, and campus facilities.',
  },
  {
    title: 'Equipment Setup',
    description:
      'Our team handles the configuration of critical equipment, such as routers, switches, and network servers, ensuring optimal performance and seamless integration.',
  },
];

const list3 = [
  {
    title: 'Advanced Network Design',
    description:
      'Utilizing GIS-based platforms to create precise and detailed network layouts, designed for cost optimization in construction and materials. These tools provide a comprehensive system of record that enhances documentation accuracy and ease of future network management.',
  },
  {
    title: 'Capacity Planning',
    description:
      'Designing scalable networks that meet today’s demands and adapt to future advancements. Our innovative capacity planning ensures seamless growth across various market segments—from single-resident homes to MDUs and enterprise businesses—supporting robust infrastructure needs.',
  },
  {
    title: 'Regulatory Compliance',
    description: 'Managing permits and ensuring adherence to local, state, and federal regulations for smooth project execution.',
  },
  {
    title: 'Structural Engineering',
    description: 'Conducting pole load analysis, foundation design, and safety assessments to ensure infrastructure durability.',
  },
  {
    title: 'Advanced Network Planning and Design',
    description:
      'Utilizing cutting-edge GIS tools, we meticulously plan and map fiber routes to optimize network performance, considering everything from environmental obstacles to infrastructure integration.',
  },
];

const list4 = [
  {
    title: 'FTTH (Fiber to the Home) Projects',
    description:
      'We specialize in delivering direct fiber installations to residential properties, providing ultra-fast, dependable internet connectivity. Our approach emphasizes precision and minimal community disruption.',
  },
  {
    title: 'Versatile FTTX Infrastructure',
    description:
      'Our tailored FTTX (Fiber to the X) services cater to diverse applications, such as homes, enterprises, or wireless towers, with scalable solutions built for future data demands.',
  },
  {
    title: 'Micro-Trenching Expertise',
    description:
      'We offer micro-trenching as a fast and minimally disruptive method for deploying fiber in dense urban areas, optimizing efficiency, and reducing construction impact on local roads.',
  },
  {
    title: 'ONT Installation and Optimization',
    description:
      'Our skilled team manages the end-to-end setup of Optical Network Terminals (ONTs), ensuring that FTTH connections deliver peak performance and seamless user experience.',
  },
];

const list5 = [
  {
    title: 'Routine Maintenance',
    description:
      'We perform regular check-ups and servicing of network components to prevent issues before they arise, ensuring system longevity and reliability.',
  },
  {
    title: 'Rapid Restoration',
    description:
      'When disruptions occur, our rapid response teams are ready to restore network functionality. Using advanced diagnostics and repair methods, we minimize downtime and get you back online quickly.',
  },
];

const list6 = [
  {
    title: 'Fiber and Copper Splicing',
    description:
      'We use the latest splicing technology to create flawless connections, reducing signal loss and enhancing network performance.',
  },
  {
    title: 'Service Activation and Testing',
    description:
      'We rigorously test and activate all systems, providing comprehensive documentation to ensure splicing meets all testing requirements. Our meticulous process guarantees that your network meets stringent performance criteria and is ready for optimal operation.',
  },
];

const Expertise: NextPage = () => {
  return (
    <>
      <Header
        title="Welcome to Utilities One – Your Partner in Wireline Construction Excellence"
        description={
          "At Utilities One, we provide top-tier wireline construction services tailored to meet the evolving needs of today's market. Our expertise spans across various critical areas, ensuring seamless integration, efficiency, and reliability in every project we undertake."
        }
        image={wirelineConstructionHeroSection}
      />
      <Box mt={15} />
      <TextBlockWithImage
        image={oSPConstr}
        title={'OSP Construction'}
        description={
          'Our OSP construction services at Utilities One cover everything from initial site surveys to the deployment of extensive fiber networks. We excel in delivering robust network infrastructure, from dense urban areas to remote regions where resources can be challenging to secure, tailored to your specific needs.'
        }
        listItems={list1}
        isLeftImage
      />
      <TextBlockWithImage
        image={iSPConstr}
        title={'ISP Construction'}
        description={
          'For internal network infrastructure, we offer precise and efficient ISP construction, which is crucial for today’s connected environments.'
        }
        listItems={list2}
      />
      <TextBlockWithImage
        image={engineeringExpertise}
        title={'Engineering Expertise'}
        description={
          'At Utilities One, our in-house engineering team ensures seamless, efficient, and scalable network designs to meet diverse project demands.'
        }
        listItems={list3}
        isLeftImage
      />
      <TextBlockWithImage
        image={comprehensiveFiberFTTXSolutions}
        title={'Comprehensive Fiber and FTTX Solutions'}
        description={
          'At Utilities One, we deliver advanced fiber and FTTX solutions, ensuring seamless, high-speed connectivity with minimal disruption.'
        }
        listItems={list4}
      />
      <TextBlockWithImage
        image={maintenanceRestoration}
        title={'Maintenance and Restoration'}
        description={'Our proactive and reactive maintenance services are designed to keep your networks running smoothly.'}
        listItems={list5}
        isLeftImage
      />
      <TextBlockWithImage
        image={precisionSplicingNetworkActivation}
        title={'Precision Splicing and Network Activation'}
        description={
          'Our technicians are highly trained in network splicing and activation, providing critical support for high-performance connections.'
        }
        listItems={list6}
      />
      <SafetyAndExcellence
        title="Commitment to Safety and Excellence"
        description={
          'Safety and quality form the backbone of our operations. Utilities One adheres to the highest safety standards, ensuring every project is executed without compromise. Our quality assurance processes ensure that every aspect of our work exceeds industry expectations.'
        }
        image={commitmentSafetyExcellence}
      />
    </>
  );
};

export default Expertise;
